import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Search, ChevronRight, ChevronLeft, Star, Check,
  Stethoscope, Calculator, X, ChevronDown, Shield, UserCheck,
  Clock
} from "lucide-react";
import AgentLayout from "@/layouts/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useListMasterCptCodes, useListProviders, useListCostEstimates,
  useCreateCostEstimate, useDeleteCostEstimate, getListCostEstimatesQueryKey,
  useGetStates, useGetLocalities, useGetCodeTypes,
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = ["#2563EB", "#22C55E"];
const INS_RATE = 0.7;

const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

const POPULAR_LOCATIONS = ["New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ"];
const STEP_LABELS = ["Billing Code Search", "State & Locality", "Price Compare Chart"];

const AVATAR_GRADIENTS = [
  "from-blue-500 to-indigo-600",
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-500",
  "from-emerald-500 to-teal-600",
  "from-rose-500 to-pink-600",
  "from-amber-500 to-orange-500",
];

const CATEGORY_COLORS: Record<string, string> = {
  "Evaluation & Management": "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  "Radiology": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  "Cardiology": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "Laboratory": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "Physical Therapy": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  "Mental Health": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "Surgery": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

type Step = 0 | 1 | 2;

interface SelectedService {
  id: number;
  code: string;
  description: string;
  price: number;
  category: string;
}

function MultiSelect({
  options = [], value = [], onChange, placeholder, localityMap = {},
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; localityMap?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = useMemo(() => {
    return (options || []).map((o: any) => {
      if (typeof o === "string") return { value: o, label: o };
      if (typeof o === "object" && o !== null) {
        const val = String(o.value ?? o.id ?? o.localityId ?? o.code ?? o.name ?? "");
        const rawLbl = o.label ?? o.localityName ?? o.name ?? o.locality ?? o.description ?? o.value ?? o.id ?? "";
        const lbl = typeof rawLbl === "object" && rawLbl !== null
          ? String(rawLbl.name || rawLbl.localityName || rawLbl.code || rawLbl.id || "Option")
          : String(rawLbl || val || "Option");
        return { value: val, label: o.label ? String(o.label) : `${lbl}${o.state ? ` (${o.state})` : ""}` };
      }
      return { value: String(o ?? ""), label: String(o ?? "") };
    });
  }, [options]);

  const filtered = useMemo(() => {
    return normalizedOptions.filter(o =>
      String(o.label).toLowerCase().includes((search || "").toLowerCase())
    );
  }, [normalizedOptions, search]);

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    const strVal = String(optValue);
    onChange(safeValue.includes(strVal) ? safeValue.filter(v => v !== strVal) : [...safeValue, strVal]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[48px] px-4 py-2 border border-slate-200 dark:border-slate-700/80 rounded-2xl bg-white dark:bg-slate-900 text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
        >
          {safeValue.length === 0 ? (
            <span className="text-slate-400 font-normal">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5 items-center flex-1 pr-2">
              {safeValue.slice(0, 3).map((v, idx) => {
                const foundOpt = normalizedOptions.find(o => o.value === v);
                let label = foundOpt?.label || localityMap?.[v];
                if (!label || label === v || v.length > 20) {
                  label = localityMap?.[v] || (foundOpt?.label && foundOpt.label !== v ? foundOpt.label : "Selected Area");
                }
                const itemKey = typeof v === 'string' ? v : `val-${idx}`;
                return (
                  <Badge key={itemKey} variant="secondary" className="text-xs rounded-lg px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 font-medium">
                    {String(label)}
                    <span
                      className="ml-1.5 cursor-pointer hover:text-destructive text-primary/70 font-bold"
                      onClick={e => { e.stopPropagation(); toggle(v); }}
                    >&times;</span>
                  </Badge>
                );
              })}
              {safeValue.length > 3 && (
                <Badge variant="outline" className="text-xs rounded-lg px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 font-semibold">
                  +{safeValue.length - 3} more
                </Badge>
              )}
            </div>
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden" align="start">
        <div className="p-2.5 border-b border-slate-100 dark:border-slate-800 space-y-2">
          <Input
            placeholder="Search localities..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-700"
          />
          <div className="flex items-center justify-between text-[11px] px-1 text-slate-500">
            <span>{safeValue.length} selected</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => onChange(filtered.map(o => o.value))} className="text-primary font-bold hover:underline">Select All</button>
              <button type="button" onClick={() => onChange([])} className="text-slate-400 hover:underline">Clear</button>
            </div>
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto p-1 divide-y divide-slate-50 dark:divide-slate-800/50">
          {filtered.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400">No matching localities</div>
          ) : (
            filtered.map(o => {
              const isSelected = safeValue.includes(o.value);
              return (
                <div
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className={`flex items-center justify-between px-3 py-2 text-xs rounded-xl cursor-pointer transition-colors ${
                    isSelected ? "bg-primary/10 text-primary font-bold" : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <span className="truncate pr-2">{o.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CostEstimator() {
  const [step, setStep] = useState<Step>(0);
  const [selectedState, setSelectedState] = useState("");
  const [selectedLocalityIds, setSelectedLocalityIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  
  const [services, setServices] = useState<SelectedService[]>([]);
  const [cptOpen, setCptOpen] = useState(false);
  const [cptSearch, setCptSearch] = useState("");
  const [selectedCodeType, setSelectedCodeType] = useState("ALL");

  const [providerSearch, setProviderSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedProviders, setSavedProviders] = useState<Set<number>>(new Set());
  const [selectedLinkedProviders, setSelectedLinkedProviders] = useState<Record<number, any>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: statesData } = useGetStates();
  const US_STATES = useMemo(() => {
    if (!statesData || !Array.isArray(statesData) || statesData.length === 0) return DEFAULT_US_STATES;
    return statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || s?.id || '')).filter(Boolean);
  }, [statesData]);

  const localityCacheRef = useRef<Record<string, string>>({});
  const { data: localitiesData } = useGetLocalities(selectedState);
  useEffect(() => {
    if (Array.isArray(localitiesData)) {
      localitiesData.forEach((loc: any) => {
        if (typeof loc === 'object' && loc !== null) {
          const id = String(loc.id ?? loc.localityId ?? loc.value ?? "");
          const name = String(loc.name ?? loc.localityName ?? loc.label ?? loc.description ?? id);
          const stateStr = loc.state ? ` (${loc.state})` : "";
          if (id) localityCacheRef.current[id] = `${name}${stateStr}`;
        }
      });
    }
  }, [localitiesData]);

  const LOCALITIES = useMemo(() => {
    if (!localitiesData || !Array.isArray(localitiesData)) return [];
    return localitiesData.map((loc: any) => {
      if (typeof loc === 'string') return { value: loc, label: loc };
      if (typeof loc === 'object' && loc !== null) {
        const val = String(loc.id ?? loc.localityId ?? loc.code ?? loc.value ?? "");
        const labelStr = String(loc.name ?? loc.localityName ?? loc.label ?? loc.description ?? val);
        const stateStr = loc.state ? ` (${loc.state})` : "";
        return { value: val, label: `${labelStr}${stateStr}` };
      }
      return { value: String(loc), label: String(loc) };
    });
  }, [localitiesData]);

  const { data: codeTypesData } = useGetCodeTypes();
  const codeTypes = useMemo(() => {
    if (!codeTypesData || !Array.isArray(codeTypesData)) return [];
    return codeTypesData.map((ct: any) => typeof ct === 'string' ? ct : String(ct?.name || ct?.code || '')).filter(Boolean);
  }, [codeTypesData]);

  const { data: cptCodesData } = useListMasterCptCodes({ limit: 1000 });
  const cptCodes = cptCodesData?.items || [];

  const { data: providersData, isLoading: providersLoading } = useListProviders(
    step === 2 ? {
      state: selectedState || undefined,
      localityIds: selectedLocalityIds.length > 0 ? selectedLocalityIds.join(",") : undefined,
      location: location || undefined,
      cptCode: services.map(s => s.code).join(","),
      page: currentPage,
      limit: itemsPerPage,
      search: providerSearch || undefined
    } : undefined,
    { query: { enabled: step === 2, queryKey: ["providers", selectedState, selectedLocalityIds, location, services.map(s => s.code).join(","), step, currentPage, itemsPerPage, providerSearch] } }
  );
  
  const providers = providersData?.items || [];
  const totalEntries = providersData?.totalCount || 0;
  const totalPages = providersData?.totalPages || 1;
  const createEstimate = useCreateCostEstimate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const activeCodes = useMemo(() =>
    (cptCodes ?? []).filter((c: any) => {
      const matchesSearch = !cptSearch ||
        c.code.toLowerCase().includes(cptSearch.toLowerCase()) ||
        c.description.toLowerCase().includes(cptSearch.toLowerCase());
      
      if (!matchesSearch) return false;
      if (selectedCodeType === "ALL") return true;

      const cType = typeof c.codeType === 'object' && c.codeType !== null
        ? ((c.codeType as any)?.name || (c.codeType as any)?.code || "")
        : String(c.codeType || c.category || "");
      return cType.toLowerCase() === selectedCodeType.toLowerCase();
    }), [cptCodes, cptSearch, selectedCodeType]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setCptOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSaveEstimate = (
    providerId: number, 
    providerName: string,
    providerType: string,
    estimatedCost: number,
    insuranceCoverage: number,
    patientResponsibility: number
  ) => {
    if (services.length === 0) return;
    setSavingId(providerId);
    createEstimate.mutate(
      { 
        data: { 
          procedure: services.map(s => s.description).join(" + "),
          cptCodes: services.map(s => s.code).join(", "),
          providerId, 
          providerName,
          providerType,
          estimatedCost,
          insuranceCoverage,
          patientResponsibility,
          insurancePlan: "StraightDoc" 
        } 
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
          setSavedProviders(s => new Set([...Array.from(s), providerId]));
          toast({ title: "Estimate saved!", description: `Saved estimate with ${providerName} for ${services.map(s => s.code).join(", ")}.` });
          setSavingId(null);
        },
        onError: () => setSavingId(null),
      }
    );
  };

  const resetFlow = () => {
    setStep(0); setSelectedState(""); setSelectedLocalityIds([]); setLocation(""); setServices([]); setCptSearch(""); setSavedProviders(new Set());
    setProviderSearch(""); setCurrentPage(1); setSelectedCodeType("ALL");
  };

  const totalCost = useMemo(() => {
    if (services.length === 0) return 150;
    const sum = services.reduce((acc, s) => {
      const rawP = Number(s.price);
      const p = isNaN(rawP) || rawP <= 0 ? 150 : rawP;
      return acc + p;
    }, 0);
    return isNaN(sum) || sum <= 0 ? 150 : sum;
  }, [services]);

  const patientCost = Math.round(totalCost * (1 - INS_RATE));
  const insuranceCost = Math.round(totalCost * INS_RATE);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <AgentLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
            <Calculator className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Good Faith Estimate (GFE)</h1>
            <p className="text-slate-400 text-sm mt-0.5">Estimate medical procedure costs and view provider rates</p>
          </div>
        </div>

        {/* Wizard Card */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">

          {/* Step tabs */}
          <div className="grid grid-cols-3 border-b border-slate-100 dark:border-slate-800">
            {STEP_LABELS.map((label, i) => {
              const done = step > i;
              const active = step === i;
              return (
                <button
                  key={i}
                  onClick={() => done ? setStep(i as Step) : undefined}
                  className={`relative flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                    active ? "text-primary" : done ? "text-slate-500 hover:text-primary cursor-pointer" : "text-slate-300 dark:text-slate-600 cursor-default"
                  }`}
                >
                  {active && (
                    <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                    active ? "bg-primary text-white shadow-md shadow-primary/30" :
                    done ? "bg-emerald-500 text-white" :
                    "bg-slate-100 dark:bg-slate-800 text-slate-400"
                  }`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                  {done && i < 2 && <ChevronRight className="w-3.5 h-3.5 text-slate-300 hidden sm:block" />}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">

            {/* ── STEP 1: Billing Code Search ── */}
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="p-7 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-xl">Search & Select Billing Codes</h2>
                    <p className="text-slate-400 text-sm mt-1">Select billing code type and search CPT / procedure codes to compare rates</p>
                  </div>
                </div>

                {/* Selected pill shown above input when a service is chosen */}
                {services.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 mb-2 flex-wrap"
                  >
                    {services.map(srv => (
                      <span key={srv.id} className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-xl px-3 py-2 text-sm max-w-full">
                        <span className="font-mono font-bold text-primary text-xs bg-primary/15 px-1.5 py-0.5 rounded-md flex-shrink-0">{srv.code}</span>
                        <span className="text-slate-700 dark:text-slate-300 truncate max-w-[200px] sm:max-w-xs">{srv.description}</span>
                        <button
                          onClick={() => setServices(prev => prev.filter(s => s.id !== srv.id))}
                          className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors ml-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </motion.div>
                )}

                {/* Code Type Filter & Procedure Search */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" ref={dropdownRef}>
                  {/* Code Type Filter Dropdown */}
                  <div className="space-y-1 md:col-span-1">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                      Code Type Filter
                    </label>
                    <Select value={selectedCodeType} onValueChange={setSelectedCodeType}>
                      <SelectTrigger className="h-13 text-sm rounded-2xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm font-medium">
                        <SelectValue placeholder="All Code Types" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl shadow-2xl z-50 max-h-60">
                        <SelectItem value="ALL">All Code Types</SelectItem>
                        {codeTypes.map((valStr: string, idx: number) => (
                          <SelectItem key={`${valStr}-${idx}`} value={valStr}>
                            {valStr}
                          </SelectItem>
                        ))}
                        {codeTypes.length === 0 && (
                          <>
                            <SelectItem value="CPT">CPT</SelectItem>
                            <SelectItem value="HCPCS">HCPCS</SelectItem>
                            <SelectItem value="ICD-10">ICD-10</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Unified procedure search input */}
                  <div className="space-y-1 md:col-span-2 relative">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                      Search Procedure / CPT Code
                    </label>
                    <div className={`relative flex items-center rounded-2xl border transition-all ${
                      cptOpen
                        ? "border-primary ring-2 ring-primary/15 bg-white dark:bg-slate-900"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-primary/40"
                    }`}>
                      <Search className={`absolute left-4 w-4 h-4 flex-shrink-0 transition-colors ${cptOpen ? "text-primary" : "text-slate-400"}`} />
                      <input
                        data-testid="select-cpt"
                        value={cptSearch}
                        onChange={e => { setCptSearch(e.target.value); setCptOpen(true); }}
                        onFocus={() => setCptOpen(true)}
                        placeholder={services.length > 0 ? "Add another procedure…" : "Search by procedure name or CPT code…"}
                        className="w-full h-13 pl-11 pr-4 bg-transparent text-sm text-slate-700 dark:text-slate-300 placeholder:text-slate-400 outline-none rounded-2xl"
                      />
                      {cptSearch && (
                        <button
                          onClick={() => { setCptSearch(""); if (services.length === 0) setCptOpen(false); }}
                          className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Results dropdown */}
                    <AnimatePresence>
                      {cptOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -4, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -4, scale: 0.98 }}
                          transition={{ duration: 0.13 }}
                          className="absolute z-50 top-full mt-2 left-0 right-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
                        >
                          <div className="max-h-72 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800/60">
                            {activeCodes.length === 0 ? (
                              <div className="py-12 text-center text-sm text-slate-400">
                                <Search className="w-7 h-7 mx-auto mb-2 opacity-20" />
                                {cptSearch ? `No procedures matching "${cptSearch}"` : "No procedures found"}
                              </div>
                            ) : activeCodes.map(c => {
                              const isSelected = services.some(s => s.id === c.id);
                              const rawPrice = c.standardPrice ?? c.price ?? c.cost;
                              const price = (typeof rawPrice === 'number' && !isNaN(rawPrice) && rawPrice > 0) ? rawPrice : 150;
                              const categoryName = typeof c.category === 'object' && c.category !== null
                                ? ((c.category as any)?.name || (c.category as any)?.code || "")
                                : String(c.category || c.codeType?.name || c.codeType || "");
                              const catColor = CATEGORY_COLORS[categoryName] ?? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400";
                              return (
                                <button
                                  key={c.id}
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={() => {
                                    if (isSelected) {
                                      setServices(prev => prev.filter(s => s.id !== c.id));
                                    } else {
                                      setServices(prev => [...prev, { id: c.id, code: c.code, description: c.description, price, category: categoryName }]);
                                    }
                                    setCptSearch("");
                                  }}
                                  className={`w-full text-left px-4 py-3.5 flex items-start justify-between gap-4 transition-colors group ${
                                    isSelected ? "bg-primary/6" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                  }`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded-md transition-colors ${
                                        isSelected
                                          ? "bg-primary/15 text-primary"
                                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover:bg-primary/10 group-hover:text-primary"
                                      }`}>
                                        {c.code}
                                      </span>
                                      {categoryName && (
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${catColor}`}>
                                          {categoryName}
                                        </span>
                                      )}
                                      {isSelected && <Check className="w-3.5 h-3.5 text-primary ml-auto" />}
                                    </div>
                                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug line-clamp-1">{c.description}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                            <p className="text-[11px] text-slate-400">{activeCodes.length} procedure{activeCodes.length !== 1 ? "s" : ""}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="flex gap-3 pt-3">
                  <Button
                    onClick={() => setStep(1)}
                    disabled={services.length === 0}
                    className="w-full h-12 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-base gap-2 shadow-lg shadow-blue-500/25 active:scale-[0.99] transition-all disabled:opacity-50 disabled:shadow-none"
                    data-testid="button-service-continue"
                  >
                    Continue to State & Locality <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 2: State & Locality Selection ── */}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="p-7 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 dark:text-white text-xl">Select Location</h2>
                    <p className="text-slate-400 text-sm mt-1">Search for providers and rates by state and locality area</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* State Select Dropdown */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                      State <span className="text-primary">*</span>
                    </label>
                    <Select
                      value={selectedState}
                      onValueChange={(val) => {
                        setSelectedState(val);
                        setSelectedLocalityIds([]);
                      }}
                    >
                      <SelectTrigger className="h-12 text-sm rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm">
                        <SelectValue placeholder="Select State (e.g. CA, NY, TX)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 z-50 rounded-2xl shadow-2xl">
                        {US_STATES.map((s: string, idx: number) => (
                          <SelectItem key={`${s}-${idx}`} value={s} className="rounded-xl my-0.5">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Primary Locality MultiSelect Dropdown */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                        Practice Location / Area
                      </label>
                      {selectedState && (
                        <span className="text-[11px] text-primary font-semibold px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                          {selectedState}
                        </span>
                      )}
                    </div>
                    <MultiSelect
                      options={LOCALITIES}
                      value={selectedLocalityIds}
                      onChange={setSelectedLocalityIds}
                      localityMap={localityCacheRef.current}
                      placeholder={
                        selectedState
                          ? `Select localities in ${selectedState}...`
                          : "Select state first to view localities..."
                      }
                    />
                  </div>

                  {/* City/Address/Zip Code (Full Width) */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider block">
                      City, Address or ZIP Code <span className="text-slate-400 font-normal lowercase">(Optional)</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (selectedState || selectedLocalityIds.length > 0 || location.trim()) && setStep(2)}
                        placeholder="e.g. Los Angeles, 90210, Main St"
                        className="pl-11 pr-10 h-12 text-sm rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm"
                        data-testid="input-location"
                      />
                      {location && (
                        <button
                          type="button"
                          onClick={() => setLocation("")}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-3 font-semibold">Popular cities</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_LOCATIONS.map(loc => (
                      <button
                        key={loc}
                        type="button"
                        onClick={() => {
                          setLocation(loc);
                          const stateMatch = loc.split(", ")[1];
                          if (stateMatch) setSelectedState(stateMatch);
                        }}
                        className={`text-sm px-4 py-2 rounded-full border font-medium transition-all ${
                          location === loc || (selectedState && loc.endsWith(selectedState))
                            ? "bg-primary text-white border-primary shadow-sm shadow-primary/20"
                            : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-primary/50 hover:text-primary hover:bg-primary/5"
                        }`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-3">
                  <Button variant="outline" onClick={() => setStep(0)} className="rounded-2xl gap-1.5 border-slate-200 dark:border-slate-700 h-12 px-6">
                    <ChevronLeft className="w-4 h-4" /> Back to Codes
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedState && selectedLocalityIds.length === 0 && !location.trim()}
                    className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-base gap-2 shadow-lg shadow-blue-500/25 active:scale-[0.99] transition-all disabled:opacity-50 disabled:shadow-none"
                    data-testid="button-location-continue"
                  >
                    Compare Provider Rates <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Providers ── */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="h-[700px] flex flex-col">
                <div className="lg:grid lg:grid-cols-[300px_1fr] lg:divide-x lg:divide-slate-100 dark:lg:divide-slate-800 flex-1 overflow-hidden">

                  {/* ── LEFT: sticky cost summary ── */}
                  <div className="p-6 space-y-5 border-b border-slate-100 dark:border-slate-800 lg:border-b-0 h-full overflow-y-auto no-scrollbar">

                    {/* Search Location details */}
                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Your Search</p>
                        <button onClick={() => setStep(0)} className="text-[11px] text-primary font-semibold hover:underline">Edit</button>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 font-medium">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{selectedState ? `State: ${selectedState}` : location || "All locations"}</span>
                      </div>
                      <div className="space-y-1.5 pt-1">
                        {services.map(s => (
                          <div key={s.id} className="flex items-start gap-2 text-xs">
                            <Stethoscope className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <span className="font-mono font-bold text-primary mr-1">{s.code}</span>
                              <span className="text-slate-600 dark:text-slate-400 truncate block">{s.description}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Estimated Cost Breakdown */}
                    <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/5 via-blue-50/50 to-indigo-50/30 dark:from-primary/10 dark:to-indigo-900/10 border border-primary/15 space-y-4">
                      <div>
                        <p className="text-xs font-bold text-primary uppercase tracking-wider">Estimated Total Cost</p>
                        <p className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
                          ${totalCost.toLocaleString()}
                        </p>
                      </div>

                      <div className="h-32 relative">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Insurance Pays", value: insuranceCost },
                                { name: "You Pay", value: patientCost }
                              ]}
                              innerRadius={36}
                              outerRadius={52}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {COLORS.map((color, idx) => (
                                <Cell key={idx} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: any) => [`$${Number(value || 0).toLocaleString()}`, ""]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-2 pt-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />
                            Insurance Pays (~70%)
                          </span>
                          <span className="font-bold text-slate-900 dark:text-white">${insuranceCost.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                            Est. Patient Pays (~30%)
                          </span>
                          <span className="font-bold text-slate-900 dark:text-white">${patientCost.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <Button variant="outline" onClick={resetFlow} className="w-full rounded-2xl h-10 text-xs font-semibold border-slate-200 text-slate-600">
                      Start New Search
                    </Button>
                  </div>

                  {/* ── RIGHT: Provider cards ── */}
                  <div className="p-6 space-y-4 h-full overflow-y-auto flex flex-col relative">

                    {/* Results header */}
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">
                          {providersLoading
                            ? "Searching providers…"
                            : `${totalEntries} provider${totalEntries !== 1 ? "s" : ""} found`
                          }
                        </h3>
                        {!providersLoading && providers.length > 0 && (
                          <span className="text-xs text-slate-400">Sorted by % of Fee Schedule</span>
                        )}
                      </div>
                      
                      {!providersLoading && providers.length > 0 && (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input 
                            value={providerSearch}
                            onChange={e => { setProviderSearch(e.target.value); setCurrentPage(1); }}
                            placeholder="Search by provider name..."
                            className="pl-9 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm focus-visible:ring-primary/30"
                          />
                        </div>
                      )}
                    </div>

                    {/* Provider cards */}
                    <div className="space-y-3 flex-1">
                    {providersLoading
                      ? Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                          <div className="flex gap-4">
                            <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-5 w-44" />
                              <Skeleton className="h-4 w-28" />
                              <Skeleton className="h-4 w-36 mt-1" />
                              <Skeleton className="h-9 w-40 mt-2 rounded-full" />
                            </div>
                          </div>
                        </div>
                      ))
                      : providers.map((prov, i) => {
                          const isSaved = savedProviders.has(prov.id);
                          const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                          const initials = String(prov.name || "Provider").split(" ").map((n: string) => n[0]).slice(0, 2).join("");
                          const isBest = i === 0;
                          
                          const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                          const linkedProviders: any[] = prov.linkedProviders || [];
                          const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);

                          let effectiveMedStr: string;
                          if (isGroupProvider && selectedLinkedProvider) {
                            effectiveMedStr = selectedLinkedProvider.medicareCharge
                              ? String(selectedLinkedProvider.medicareCharge).replace('%', '')
                              : '100';
                          } else {
                            effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                          }
                          
                          const parsedMed = parseFloat(effectiveMedStr);
                          const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 1 : parsedMed / 100;
                          const safeTotal = isNaN(totalCost) || totalCost <= 0 ? 150 : totalCost;
                          const rawProvPrice = Math.round(safeTotal * medPercent);
                          const provPrice = isNaN(rawProvPrice) || rawProvPrice <= 0 ? 150 : rawProvPrice;
                          const provPatientCost = provPrice;

                          const primaryAddress = prov.addresses?.find((a: any) => a.isPrimary) || prov.addresses?.[0];
                          const addressString = primaryAddress 
                            ? `${primaryAddress.street1}${primaryAddress.street2 ? ' ' + primaryAddress.street2 : ''}, ${primaryAddress.city}, ${primaryAddress.state} ${primaryAddress.zipCode}` 
                            : (prov.address || `${selectedState || location}`);

                          return (
                            <motion.div
                              key={prov.id}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.06, duration: 0.2 }}
                              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                                isSaved
                                  ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/40 dark:bg-emerald-900/10"
                                  : isBest
                                    ? "border-primary/25 bg-white dark:bg-slate-900 shadow-md shadow-primary/5"
                                    : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/20 hover:shadow-sm"
                              }`}
                            >
                              <div className="flex flex-col">
                                <div className="p-4 md:p-5 flex flex-col md:flex-row gap-5">
                                  {/* Main Content (Avatar + Details) */}
                                  <div className="flex-1 flex flex-col sm:flex-row items-start gap-5 min-w-0">
                                    {/* Avatar */}
                                    <div className="flex-shrink-0 relative">
                                      <div className={`w-[72px] h-[72px] md:w-[84px] md:h-[84px] rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-3xl font-medium shadow-sm ring-4 ring-white dark:ring-slate-900`}>
                                        {initials}
                                      </div>
                                      <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0 mt-1">
                                      {isBest && !isSaved && (
                                        <div className="inline-flex items-center gap-1 text-[#D97706] bg-[#FEF3C7] text-[11px] font-bold px-2 py-0.5 rounded-full mb-2 uppercase tracking-wide">
                                          <Check className="w-3 h-3" /> Top Match
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between gap-4">
                                        <div>
                                          <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight truncate">{prov.name}</h3>
                                          <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 mt-0.5">{prov.headline || (typeof prov.specialty === 'object' && prov.specialty !== null ? (prov.specialty.name || prov.specialty.title || "") : String(prov.specialty || ""))}{prov.gender ? <span className="text-slate-400 font-normal"> · {prov.gender}</span> : ""}</p>
                                        </div>
                                      </div>

                                      {/* Badges */}
                                      {(() => {
                                        const rawMedCharge = typeof prov.medicareCharge === 'object' && prov.medicareCharge !== null
                                          ? ((prov.medicareCharge as any)?.name || (prov.medicareCharge as any)?.rate || "")
                                          : String(prov.medicareCharge || "");
                                        const medicareChargeText = rawMedCharge ? (rawMedCharge.includes('%') ? rawMedCharge : rawMedCharge + '%') : "100% of Fee Schedule";
                                        const hospitalName = typeof prov.hospital === 'object' && prov.hospital !== null
                                          ? ((prov.hospital as any)?.name || (prov.hospital as any)?.title || (prov.hospital as any)?.code || "")
                                          : String(prov.hospital || "");

                                        return (
                                          <>
                                            <div className="flex flex-wrap items-center gap-2 mt-3">
                                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-semibold border border-amber-200/50 dark:border-amber-800/50">
                                                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                                <span>{(prov.rating || 0).toFixed(1)} <span className="text-amber-600/70 dark:text-amber-500/70 font-medium">({prov.reviewCount || 0})</span></span>
                                              </div>
                                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium border border-slate-200 dark:border-slate-700">
                                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                {prov.yearsExperience || 5} yrs exp
                                              </div>
                                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-semibold border border-emerald-200/50 dark:border-emerald-800/50">
                                                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                                                {medicareChargeText}
                                              </div>
                                            </div>

                                            {/* Location & Time */}
                                            <div className="mt-4 space-y-2">
                                              <div className="flex items-start gap-2.5 text-[13px] text-slate-600 dark:text-slate-400">
                                                <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                                <span className="leading-snug">
                                                  {hospitalName && <><span className="font-medium text-slate-900 dark:text-slate-200">{hospitalName}</span><br/></>}
                                                  {addressString}
                                                </span>
                                              </div>
                                            </div>
                                          </>
                                        );
                                      })()}

                                      {/* Group: Linked Provider Selector */}
                                      {isGroupProvider && linkedProviders.length > 0 && (() => {
                                        const primaryAddress = prov.addresses?.find((a: any) => a.isPrimary) || prov.addresses?.[0];
                                        const currentLocId = primaryAddress?.id ? String(primaryAddress.id) : `loc_1`;

                                        const filteredLinkedProviders = linkedProviders.filter((lp: any) => {
                                          const assignedIds: string[] = lp.assignedLocationIds || [];
                                          if (assignedIds.length === 0) return true;
                                          return assignedIds.includes(currentLocId) || assignedIds.includes(`loc_1`);
                                        });

                                        const displayProviders = filteredLinkedProviders.length > 0 ? filteredLinkedProviders : linkedProviders;

                                        return (
                                          <div className="mt-4 inline-block max-w-full p-3 rounded-2xl bg-indigo-50/40 dark:bg-indigo-900/10 border border-indigo-100/50 dark:border-indigo-800/30">
                                            <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                              <UserCheck className="w-3.5 h-3.5" /> Select Provider
                                            </p>
                                            <div className="flex overflow-x-auto scrollbar-hide gap-1.5 pb-0.5">
                                              {displayProviders.map((lp: any) => {
                                                const isSelected = (selectedLinkedProvider?.id === lp.id) || (!selectedLinkedProvider && displayProviders.indexOf(lp) === 0);
                                                const lpMed = lp.medicareCharge ? String(lp.medicareCharge).replace('%','') : '100';
                                                const lpName = typeof lp === 'object' && lp !== null
                                                  ? ((lp as any)?.name || (lp.user?.firstName ? `${lp.user.firstName} ${lp.user.lastName}` : "Provider"))
                                                  : String(lp || "Provider");
                                                return (
                                                  <button
                                                    key={lp.id}
                                                    onClick={() => setSelectedLinkedProviders(prev => ({ ...prev, [prov.id]: lp }))}
                                                    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-200 ${
                                                      isSelected
                                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-600/20'
                                                        : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:text-indigo-600 hover:shadow-sm'
                                                    }`}
                                                  >
                                                    <span className="truncate max-w-[140px]">{String(lpName)}</span>
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'}`}>
                                                      {lpMed}%
                                                    </span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>

                                  {/* Right side Pricing & CTA */}
                                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-800 pt-4 md:pt-0 md:pl-6 min-w-[180px] gap-3">
                                    <div className="text-left md:text-right">
                                      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Patient pays</p>
                                      <p className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 tracking-tight">
                                        ${provPatientCost.toLocaleString()}
                                      </p>
                                    </div>

                                    <div className="flex flex-col gap-2 w-full max-w-[180px]">
                                      <Button
                                        onClick={() => handleSaveEstimate(
                                          prov.id,
                                          prov.name,
                                          prov.specialty || "Provider",
                                          provPrice,
                                          Math.round(provPrice * INS_RATE),
                                          provPatientCost
                                        )}
                                        disabled={isSaved || savingId === prov.id}
                                        className={`w-full h-10 rounded-xl text-xs font-bold gap-1.5 transition-all shadow-sm ${
                                          isSaved
                                            ? "bg-emerald-600 text-white border-0"
                                            : "bg-primary text-white hover:bg-primary/90 shadow-primary/20"
                                        }`}
                                      >
                                        {savingId === prov.id ? (
                                          "Saving…"
                                        ) : isSaved ? (
                                          <><Check className="w-4 h-4" /> Save Estimate</>
                                        ) : (
                                          <><Check className="w-4 h-4" /> Save Estimate</>
                                        )}
                                      </Button>

                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          const slug = prov.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                                          window.open(`/provider/${slug}/${prov.id}`, '_blank');
                                        }}
                                        className="w-full h-10 rounded-xl text-xs font-semibold border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                                      >
                                        View Profile
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
                        <div className="flex items-center gap-2 text-slate-500">
                          <span>Rows per page:</span>
                          <Select value={String(itemsPerPage)} onValueChange={v => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                            <SelectTrigger className="h-7 w-16 text-xs rounded-lg border-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                            </SelectContent>
                          </Select>
                          <span>Showing {(currentPage-1)*itemsPerPage + 1} to {Math.min(currentPage*itemsPerPage, totalEntries)} of {totalEntries} entries</span>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center disabled:opacity-40"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>

                          {getPageNumbers().map((p, idx) => typeof p === 'number' ? (
                            <button
                              key={idx}
                              onClick={() => setCurrentPage(p)}
                              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${currentPage === p ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-600'}`}
                            >
                              {p}
                            </button>
                          ) : (
                            <span key={idx} className="px-1 text-slate-400">…</span>
                          ))}

                          <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center disabled:opacity-40"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </AgentLayout>
  );
}
